/**
 * @file hooks/biz/list/action 5种列表页常见操作
 * @author zhangzhe(zhangzhe@baidu.com)
 */

import _ from 'lodash';
import {useCallback} from 'react';

import {ajaxAction} from '../../../components/biz/HooksList/action/ajaxAction';
import {dialogAlertAction} from '../../../components/biz/HooksList/action/dialogAlertAction';
import {dialogPlainAction} from '../../../components/biz/HooksList/action/dialogPlainAction';
import {dialogCommonAction} from '../../../components/biz/HooksList/action/dialogCommonAction';
import {drawerAction} from '../../../components/biz/HooksList/action/drawerAction';
import {createParams} from '../../../components/biz/common/helper';

import useDialog from '../dialog';
import useConfirm from '../confirm';
import {ACTION_TYPE, AjaxOption, DialogOption, DrawerOption} from './interface';

export interface ActionConfig {
    actionType: ACTION_TYPE;
    link?: string; // actionType === 'link'时的链接
    ajax?: AjaxOption; // // actionType === 'ajax'时的额外配置
    dialog?: DialogOption; // actionType === 'dialog'时的dialog配置
    drawer?: DrawerOption; // actionType === 'dialog'时的drawer配置
    action?: string; // actionType === 'custom'的用户自定义方法名
    [other: string]: any;
}

const useAction = (
    props: any,
    allFunctions: any, // 用户在业务端定义的自定义方法集合
    getSelectedItems: () => object // 获得列表中选中数据的方法
) => {
    const {history, actions} = props;

    const {Confirm, setConfirmOptions} = useConfirm();

    // 使用dialog
    const {Dialog, dialogOpen, dialogOptions, setDialogOptions, handleDialogCancel} = useDialog();

    // 页面跳转操作
    const handleRedirect = useCallback((url: string) => {
        if (history) {
            history.push(url);
        }
        else {
            window.location.href = url;
        }
    }, [history]);

    // 弹框操作
    const dialogActionType = useCallback((config: any, payload = {}) => {
        const body = config.dialog.body;
        let dialogConfig: ReturnType<typeof dialogPlainAction | typeof dialogCommonAction> | null = null;
        switch (body.type) {
            case 'alert':
                dialogAlertAction(config.dialog, payload);
                break;
            case 'plain':
                // 这最后一个参数理论上是不需要的，但为了编译通过硬加的 FIXME: zhangzhe 2018-11-30
                dialogConfig = dialogPlainAction(config.dialog, payload);
                break;
            case 'common':
                dialogConfig = dialogCommonAction(config.dialog, payload);
                break;
            default:
                throw new Error('Unsupported dialog type = ' + body.type);
        }
        if (dialogConfig) {
            setDialogOptions({...dialogConfig, type: 'dialog'});
        }
    }, [setDialogOptions]);

    // 抽屉打开
    const drawerActionType = useCallback((config: any, payload = {}) => {
        const drawerConfig = drawerAction(config.drawer, payload);
        setDialogOptions({...drawerConfig, type: 'drawer'});
    }, [setDialogOptions]);

    // 自定义操作
    const handleCustomAction = useCallback((functionName: string, payload: any = {}) => {
        // 特殊的，刷新列表按钮
        if (functionName === 'loadList') {
            allFunctions.loadList(); // 所有筛选条件不变，刷新列表
        }
        // 特殊方法，全选列表选项
        else if (functionName === 'checkAll') {
            allFunctions.checkAll(payload.checked);
        }
        else if (allFunctions) {
            const excuteFunction = allFunctions[functionName];
            excuteFunction && excuteFunction(payload);
        }
    }, [allFunctions]);

    const handleAction = useCallback((config: ActionConfig, payload = {}) => {
        if (config.select && getSelectedItems) {
            // 选中的表格数据
            config.$extraPayload = getSelectedItems();
        }
        const mergePayload = _.extend({}, payload, config.$extraPayload);
        switch (config.actionType as ACTION_TYPE) {
            case 'link':
                const {$payloadFields, $extraPayload} = config;
                const params = createParams(payload, $payloadFields, $extraPayload);
                handleRedirect(config.link + (params ? `/${params}` : ''));
                break;
            case 'dialog':
                dialogActionType(config, mergePayload);
                break;
            case 'drawer':
                drawerActionType(config, mergePayload);
                break;
            case 'ajax':
                ajaxAction(
                    {loadList: allFunctions.loadList, confirm: setConfirmOptions},
                    config.ajax as AjaxOption,
                    mergePayload,
                    actions
                );
                break;
            case 'custom':
                handleCustomAction(config.action as string, mergePayload);
                break;
            default:
                break;
        }
    }, [
        allFunctions,
        actions,
        setConfirmOptions,
        dialogActionType,
        handleCustomAction,
        drawerActionType,
        getSelectedItems,
        handleRedirect
    ]);

    // 弹框成功之后逻辑
    const handleDialogSuccess = useCallback((result?: any) => {
        allFunctions.onDialogSuccess && allFunctions.onDialogSuccess(result);
        const fromTable = _.get(dialogOptions, 'fromTable', false);
        if (fromTable) {
            // 不跳转到第一页
            allFunctions.loadList();
        }
        else {
            allFunctions.loadFirstPage();
        }
    }, [allFunctions, dialogOptions]);

    return {
        handleAction, // 核心方法：处理5种列表页常见操作
        confirmProps: {
            Confirm,
            setConfirmOptions
        },
        dialogProps: {
            Dialog,
            dialogOpen,
            dialogOptions,
            onDialogCancel: handleDialogCancel,
            onDialogSuccess: handleDialogSuccess
        }
    };
};

export default useAction;
